import {
  Checkbox as HeadlessCheckbox,
  Field as HeadlessField,
  Label as HeadlessLabel,
} from '@headlessui/react';
import { ComponentProps } from 'react';
import { cn } from '../../lib/classNames';

/**
 * Small wrapper styling HeadlessUI's components. For docs and usage, see:
 * https://headlessui.com/react/checkbox
 *
 * @example
 * import { Checkbox, CheckboxField, CheckboxLabel } from '@otovo/shared/components/Form/CheckboxField';
 *
 * <CheckboxField>
 *   <Checkbox />
 *   <CheckboxLabel>Accept terms</CheckboxLabel>
 * </CheckboxField>
 */

export const Checkbox = ({
  className,
  ...rest
}: ComponentProps<typeof HeadlessCheckbox>) => (
  <HeadlessCheckbox
    className={cn(
      'group block size-6 shrink-0 cursor-pointer rounded border bg-white transition-colors focus:ring-2 focus:ring-digital focus:ring-offset-2 focus:outline-none data-checked:bg-green-50',
      className,
    )}
    {...rest}
  >
    <svg
      className="-rotate-3 text-white opacity-0 transition-opacity delay-75 group-data-checked:opacity-100"
      viewBox="3 3 14 14"
      fill="none"
    >
      <path
        fill="currentColor"
        fillRule="evenodd"
        d="M14.63 8.38c.49-.49.49-1.28 0-1.77a1.25 1.25 0 0 0-1.77 0l-4.12 4.12-1.62-1.62a1.25 1.25 0 0 0-1.77 0 1.25 1.25 0 0 0 0 1.77l2.5 2.5c.49.49 1.28.49 1.77 0l5-5Z"
      />
    </svg>
  </HeadlessCheckbox>
);

export const CheckboxField = ({
  children,
  className,
  ...rest
}: ComponentProps<typeof HeadlessField>) => (
  <HeadlessField
    className={cn('flex items-start gap-3 rounded-md', className)}
    {...rest}
  >
    {children}
  </HeadlessField>
);

export const CheckboxLabel = ({
  className,
  children,
  ...rest
}: ComponentProps<typeof HeadlessLabel>) => (
  <HeadlessLabel
    className={cn('cursor-pointer font-medium text-subheading', className)}
    {...rest}
  >
    {children}
  </HeadlessLabel>
);
